import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import { ChatSession } from 'interfaces/chat.interface'
import Button from 'components/button/button'

type ChatSessionManagerProps = {
  sessions: ChatSession[]
  currentSession: ChatSession | null
  onSessionSelect: (session: ChatSession) => void
  onCreateSession: (topic: string) => void
  onDeleteSession: (sessionId: string) => void
  onClose: () => void
  openNewSessionForm: boolean
  openChatSessions: boolean
  isLoading?: boolean
}

const ChatSessionManager: React.FC<ChatSessionManagerProps> = ({
  sessions,
  currentSession,
  onSessionSelect,
  onCreateSession,
  onDeleteSession,
  onClose,
  openNewSessionForm,
  openChatSessions,
  isLoading = false,
}) => {
  const [newSessionTopic, setNewSessionTopic] = useState('')

  const handleCreateSession = () => {
    // Guard against duplicate submissions: the button is disabled while
    // isLoading, but the Enter-key path bypasses that, so we check here too.
    if (isLoading) return
    // Topic is optional — Sherpa discovers the topic through conversation.
    // Pass empty string through and let the chat layer auto-generate a default.
    onCreateSession(newSessionTopic.trim())
    setNewSessionTopic('')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="h-[81px] px-8 flex items-center justify-between border-b border-[#E8E8E8]">
        <h3 className="text-2xl font-semibold text-brand">
          Chat History
        </h3>
        <button
          onClick={() => {
            onClose()
            setNewSessionTopic('')
          }}
          className="hover:bg-border rounded-full p-1 cursor-pointer"
          title="Close session manager"
        >
          <svg
            className="w-5 h-5 text-text-muted"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* New Session Form */}
      {openNewSessionForm && (
        <div className="p-4">
          <input
            type="text"
            placeholder="Topic (optional)"
            value={newSessionTopic}
            onChange={e => setNewSessionTopic(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg text-xs font-normal text-text-heading placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
            onKeyDown={e => e.key === 'Enter' && handleCreateSession()}
          />
          <div className="flex mt-3">
            <Button
              variant="primary"
              onClick={handleCreateSession}
              disabled={isLoading}
              className="!min-w-0 !h-[36px] !px-5 !text-xs"
            >
              Create
            </Button>
          </div>
        </div>
      )}

      {/* Sessions List */}
      {openChatSessions && (
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {sessions.length === 0 ? (
            <div className="text-center text-text-muted py-3 text-xs">
              No previous sessions
            </div>
          ) : (
            sessions.map(session => (
              <div
                key={session.session_id}
                className={`flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                  currentSession?.session_id === session.session_id
                    ? 'bg-brand text-white'
                    : 'border border-border bg-white hover:bg-surface-muted text-text-heading'
                }`}
                onClick={() => onSessionSelect(session)}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {session.topic || 'General Chat'}
                  </div>
                  <div className={`text-[10px] mt-0.5 ${
                    currentSession?.session_id === session.session_id
                      ? 'text-white/70'
                      : 'text-text-muted'
                  }`}>
                    {session.message_count} messages • {formatDate(session.created_at)}
                  </div>
                </div>

                {currentSession?.session_id !== session.session_id && (
                  <button
                    onClick={e => {
                      e.stopPropagation()
                      onDeleteSession(session.session_id)
                    }}
                    className="ml-2 p-1 text-text-muted hover:text-error rounded-full hover:bg-border transition-colors"
                    title="Delete session"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default ChatSessionManager
